import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { Download, Loader2, Sun } from "lucide-react";

export default function PublicOfferta() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/solar/${token}`);
        setData(data);
      } catch (err) {
        setError(err?.response?.data?.detail || "Link non valido o scaduto");
      }
    })();
  }, [token]);

  useEffect(() => {
    if (data?.name) document.title = `${data.name} – Nureal-Operation`;
    return () => { document.title = "Nureal-Operation"; };
  }, [data?.name]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#FAFAFA" }}>
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-10 text-center" data-testid="offerta-error">
          <div className="text-5xl mb-3">🔒</div>
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Manrope" }}>Accesso non disponibile</h1>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2 text-sm"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento…</div>
      </div>
    );
  }

  const pdfUrl = `${API}/public/solar/${token}/pdf`;
  const falde = data.falde || [];
  const totals = data.totals || {};

  return (
    <div className="min-h-screen" style={{ background: "#FAFAFA" }} data-testid="offerta-page">
      <section className="landing-bg">
        <div className="max-w-4xl mx-auto px-6 pt-16 pb-24">
          <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-6" style={{ color: "var(--primary)" }}>
            <Sun className="w-3.5 h-3.5" /> Progetto impianto FV
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight max-w-2xl" style={{ fontFamily: "Manrope" }} data-testid="offerta-title">
            {data.name || "Impianto fotovoltaico"}
          </h1>
          {data.address && <p className="mt-3 text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>{data.address}</p>}
          <a href={pdfUrl} target="_blank" rel="noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-[color:var(--primary)] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[color:var(--primary-hover)] transition-all"
            data-testid="offerta-download-pdf">
            <Download className="w-4 h-4" /> Scarica PDF impianto
          </a>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 -mt-12 relative z-10 space-y-8 pb-16">
        {data.render && (
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-4 md:p-6">
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "Manrope" }}>Vista dall'alto (foto aerea)</h2>
            <img src={data.render} alt="Vista aerea impianto" className="w-full rounded-xl border" style={{ borderColor: "var(--border)" }} data-testid="offerta-render" />
            <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>Foto aerea con le falde in verde, la misura di ogni lato in cm e i moduli posizionati.</p>
          </div>
        )}
        {data.render_tech && (
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-4 md:p-6">
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "Manrope" }}>Disegno tecnico</h2>
            <img src={data.render_tech} alt="Disegno tecnico impianto" className="w-full rounded-xl border" style={{ borderColor: "var(--border)" }} data-testid="offerta-render-tech" />
          </div>
        )}
        {data.capture_3d && (
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-4 md:p-6">
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "Manrope" }}>Vista 3D</h2>
            <img src={data.capture_3d} alt="Vista 3D impianto" className="w-full rounded-xl border" style={{ borderColor: "var(--border)" }} data-testid="offerta-render-3d" />
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Kpi label="Falde" value={totals.falde ?? falde.length} />
            <Kpi label="Area totale" value={`${totals.area ?? 0} m²`} />
            <Kpi label="Moduli" value={totals.panels ?? 0} />
            {data.kwp != null && <Kpi label="Potenza" value={`${data.kwp} kWp`} />}
          </div>

          {falde.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="offerta-falde-table">
                <thead>
                  <tr className="text-left" style={{ color: "var(--text-muted)" }}>
                    <th className="py-2">Falda</th>
                    <th className="py-2">Lati (cm)</th>
                    <th className="py-2 text-right">Area (m²)</th>
                    <th className="py-2 text-right">Moduli</th>
                  </tr>
                </thead>
                <tbody>
                  {falde.map((f) => (
                    <tr key={f.n} className="border-t" style={{ borderColor: "var(--border)" }}>
                      <td className="py-2 font-semibold">Falda {f.n}</td>
                      <td className="py-2">{(f.sides_cm || []).join("  ·  ")}</td>
                      <td className="py-2 text-right">{f.area_m2}</td>
                      <td className="py-2 text-right">{f.panels}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2" style={{ borderColor: "var(--primary)" }}>
                    <td colSpan="2" className="py-3 font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Totale</td>
                    <td className="py-3 text-right text-lg font-extrabold" style={{ color: "var(--primary)" }}>{totals.area} m²</td>
                    <td className="py-3 text-right font-extrabold" style={{ color: "var(--primary)" }}>{totals.panels}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>Nessuna falda disegnata.</p>
          )}
        </div>
      </section>

      <footer className="border-t py-8 text-center text-xs" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
        Documento condiviso tramite Nureal-Operation
      </footer>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-xl p-3 border" style={{ borderColor: "var(--border)", background: "var(--surface-muted)" }}>
      <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--primary)" }}>{label}</div>
      <div className="mt-1 text-lg font-extrabold" style={{ fontFamily: "Manrope" }}>{value}</div>
    </div>
  );
}
